import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useCommentsAndLocations } from "../hooks/commentsAndResidenceQuery";
import CommentsLocationsDrawer from "../components/ui/drawner/CommentsLocationsDrawer";
import { useQueryClient } from "@tanstack/react-query";
import {
  NotificationSeverity,
  useNotification,
} from "../context/NotificationContext";
import StreamingMedia from "../utils/StreamingMedia";
import EditIcon from "../assets/svg/view/edit.svg";
import EditLightIcon from "../assets/svg/view/edit_ligh.svg";
import "../assets/scss/views/UserProfileInfo.scss";

const UserProfileInfo = ({
  user,
  canEditProfile,
  onEditClick,
  profileImageId,
  theme,
}) => {
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();
  const {
    useUserLocations,
    useCommentsByReference,
    createLocation,
    updateLocation,
    deleteLocation,
    createComment,
    updateComment,
    deleteComment,
  } = useCommentsAndLocations();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState(null);

  // Obtener datos
  const { data: comments } = useCommentsByReference(user?.id);
  const { data: locations, isError } = useUserLocations(user?.id);

  const locationData = locations?.data || [];
  const commentData = comments?.data || [];

  // Funciones de utilidad para formateo de datos
  const formatPhoneNumber = (countryCode, phoneNumber) => {
    if (!phoneNumber) return "No disponible";
    return `${countryCode || ""} ${phoneNumber}`.trim();
  };

  const formatFullName = (user) => {
    if (!user) return "Usuario";
    const { first_name, first_last_name, second_last_name } = user;
    return `${first_name || ""} ${first_last_name || ""} ${
      second_last_name || ""
    }`.trim();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No disponible";
    try {
      return new Date(dateString).toLocaleDateString("es-CO", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "Fecha inválida";
    }
  };

  // Función para formatear valores booleanos con etiquetas descriptivas
  const formatBooleanValue = (value, activeLabel = "Sí", inactiveLabel = "No") => {
    if (value === null || value === undefined) return "No disponible";
    return value ? activeLabel : inactiveLabel;
  };

  // Función para obtener el estado visual de un valor booleano
  const getBooleanStatus = (value) => {
    if (value === null || value === undefined) return "neutral";
    return value ? "active" : "inactive";
  };

  // Obtener el estado del usuario
  const getUserStatus = () => {
    if (user?.active_status) {
      return { status: "Activo", className: "active" };
    }
    return { status: "Inactivo", className: "inactive" };
  };

  // Función para mostrar el cargo con formato adecuado
  const formatJobPosition = (jobPosition) => {
    if (!jobPosition) return "No especificado";
    return jobPosition;
  };

  // Función para formatear el departamento
  const formatDepartment = (department) => {
    if (!department) return "No asignado";
    return department;
  };

  // Manejadores para drawer (mantenidos sin cambios)
  const handleOpenDrawer = (type) => {
    setDrawerType(type);
    setDrawerOpen(true);
  };

  const handleEdit = async (id, item) => {
    try {
      if (drawerType === "locations") {
        await updateLocation({
          locationId: id,
          data: { ...item, user_id: user?.id },
        });
        showNotification({
          message: "Ubicación actualizada exitosamente",
          severity: NotificationSeverity.SUCCESS,
        });
      } else {
        await updateComment({
          commentId: id,
          data: { ...item, reference_id: user?.id },
        });
        showNotification({
          message: "Comentario actualizado exitosamente",
          severity: NotificationSeverity.SUCCESS,
        });
      }
      queryClient.invalidateQueries([drawerType, user?.id]);
    } catch (error) {
      console.error("Error al editar:", error);
      showNotification({
        message:
          error.response?.data?.message ||
          `Error al actualizar ${
            drawerType === "locations" ? "la ubicación" : "el comentario"
          }`,
        severity: NotificationSeverity.ERROR,
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      if (drawerType === "locations") {
        await deleteLocation(id);
        showNotification({
          message: "Ubicación eliminada exitosamente",
          severity: NotificationSeverity.SUCCESS,
        });
      } else {
        await deleteComment(id);
        showNotification({
          message: "Comentario eliminado exitosamente",
          severity: NotificationSeverity.SUCCESS,
        });
      }
      queryClient.invalidateQueries([drawerType, user?.id]);
    } catch (error) {
      console.error("Error al eliminar:", error);
      showNotification({
        message:
          error.response?.data?.message ||
          `Error al eliminar ${
            drawerType === "locations" ? "la ubicación" : "el comentario"
          }`,
        severity: NotificationSeverity.ERROR,
      });
    }
  };

  const handleCreate = async (item) => {
    try {
      if (drawerType === "locations") {
        await createLocation({ ...item, user_id: user?.id });
        showNotification({
          message: "Ubicación creada exitosamente",
          severity: NotificationSeverity.SUCCESS,
        });
      } else {
        await createComment({ ...item, reference_id: user?.id });
        showNotification({
          message: "Comentario creado exitosamente",
          severity: NotificationSeverity.SUCCESS,
        });
      }
      queryClient.invalidateQueries([drawerType, user?.id]);
    } catch (error) {
      console.error("Error al crear:", error);
      showNotification({
        message:
          error.response?.data?.message ||
          `Error al crear ${
            drawerType === "locations" ? "la ubicación" : "el comentario"
          }`,
        severity: NotificationSeverity.ERROR,
      });
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    if (drawerType) {
      queryClient.invalidateQueries([drawerType, user?.id]);
    }
  };

  // Manejo de errores para la carga de datos
  useEffect(() => {
    if (isError) {
      showNotification({
        message: "Error al cargar los datos de ubicación",
        severity: NotificationSeverity.ERROR,
      });
    }
  }, [isError, showNotification]);

  // Selección dinámica del ícono según el tema
  const editIconSrc = theme === "dark" ? EditLightIcon : EditIcon;
  const userStatusInfo = getUserStatus();

  return (
    <div className={`GM__${theme}-user-profile-info`}>
      {canEditProfile && (
        <button
          className={`GM__${theme}-edit-button`}
          onClick={onEditClick}
          title="Editar perfil"
        >
          <img src={editIconSrc} alt="Editar icon" />
        </button>
      )}

      <div className={`GM__${theme}-profile-container`}>
        {/* Header con imagen y badges - Sin cambios */}
        <div className={`GM__${theme}-profile-header`}>
          <div className={`GM__${theme}-user-image-container`}>
            <div className={`GM__${theme}-image-circle`}>
              <StreamingMedia
                fileId={profileImageId}
                className={`GM__${theme}-user-image`}
              />
            </div>
          </div>

          <div className={`GM__${theme}-header-content`}>
            <h2 className={`GM__${theme}-user-title`}>
              {formatFullName(user)}
            </h2>
            <div className={`GM__${theme}-title-underline`}></div>
            <div className={`GM__${theme}-badges-container`}>
              <div className={`GM__${theme}-role-badge`}>
                {user?.role_name || "Sin rol"}
              </div>
              <div className={`GM__${theme}-status-badge ${userStatusInfo.className}`}>
                {userStatusInfo.status}
              </div>
              {/* Nuevo badge para el departamento */}
              {user?.department && (
                <div className={`GM__${theme}-department-badge`}>
                  {formatDepartment(user.department)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Información de contacto principal - Sin cambios */}
        <div className={`GM__${theme}-contact-section`}>
          <h3 className={`GM__${theme}-section-title`}>
            Información de Contacto Principal
          </h3>
          <div className={`GM__${theme}-contact-content`}>
            <div className={`GM__${theme}-contact-field-group`}>
              <div className={`GM__${theme}-contact-field`}>
                <span className={`GM__${theme}-contact-label`}>
                  Email:
                </span>
                <span className={`GM__${theme}-contact-value`}>
                  {user?.email || "No disponible"}
                </span>
              </div>
              <div className={`GM__${theme}-contact-field`}>
                <span className={`GM__${theme}-contact-label`}>
                  Teléfono principal:
                </span>
                <span className={`GM__${theme}-contact-value`}>
                  {formatPhoneNumber(
                    user?.primary_phone_code,
                    user?.primary_phone_number
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Grid de información expandido */}
        <div className={`GM__${theme}-info-grid`}>
          {/* Información personal básica - Actualizada */}
          <div className={`GM__${theme}-info-card`}>
            <h3 className={`GM__${theme}-card-title`}>
              Información Personal
            </h3>
            <div className={`GM__${theme}-card-content`}>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Tipo de Documento:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {user?.document_type_name || "No disponible"}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Número de Documento:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {user?.document_number || "No disponible"}
                  </span>
                </div>
              </div>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Fecha de Expedición:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {formatDate(user?.document_exp_date)}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Lugar de Expedición:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {user?.document_expedition_place || "No disponible"}
                  </span>
                </div>
              </div>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Fecha de Nacimiento:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {formatDate(user?.birth_date)}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Nacionalidad:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {user?.nationality_country || "No disponible"}
                  </span>
                </div>
              </div>
              {/* Nueva fila para tipo de sangre */}
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Tipo de Sangre:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {user?.blood_type || "No especificado"}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  {/* Campo vacío para mantener la estructura visual */}
                </div>
              </div>
            </div>
          </div>

          {/* NUEVA SECCIÓN: Información laboral */}
          <div className={`GM__${theme}-info-card`}>
            <h3 className={`GM__${theme}-card-title`}>
              Información Laboral
            </h3>
            <div className={`GM__${theme}-card-content`}>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Departamento:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {formatDepartment(user?.department)}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Cargo/Posición:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {formatJobPosition(user?.job_position)}
                  </span>
                </div>
              </div>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Tipo de Flota:
                  </span>
                  <span className={`GM__${theme}-field-value ${getBooleanStatus(user?.own_fleet)}`}>
                    {formatBooleanValue(user?.own_fleet, "Flota Propia", "Flota Externa")}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Notificaciones:
                  </span>
                  <span className={`GM__${theme}-field-value ${getBooleanStatus(user?.limited_notifications)}`}>
                    {formatBooleanValue(user?.limited_notifications, "Habilitadas", "Deshabilitadas")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Información de contacto detallada - Actualizada */}
          <div className={`GM__${theme}-info-card`}>
            <h3 className={`GM__${theme}-card-title`}>
              Información de Contacto Completa
            </h3>
            <div className={`GM__${theme}-card-content`}>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field-full`}>
                  <span className={`GM__${theme}-field-label`}>
                    Email:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {user?.email || "No disponible"}
                  </span>
                </div>
              </div>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Teléfono Principal:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {formatPhoneNumber(
                      user?.primary_phone_code,
                      user?.primary_phone_number
                    )}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Teléfono Secundario:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {formatPhoneNumber(
                      user?.secondary_phone_code,
                      user?.secondary_phone_number
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* NUEVA SECCIÓN: Contacto de emergencia */}
          <div className={`GM__${theme}-info-card`}>
            <h3 className={`GM__${theme}-card-title`}>
              Contacto de Emergencia
            </h3>
            <div className={`GM__${theme}-card-content`}>
              {user?.emergency_contact_name || user?.emergency_contact_phone ? (
                <>
                  <div className={`GM__${theme}-field-group`}>
                    <div className={`GM__${theme}-field-full`}>
                      <span className={`GM__${theme}-field-label`}>
                        Nombre del Contacto:
                      </span>
                      <span className={`GM__${theme}-field-value`}>
                        {user?.emergency_contact_name || "No especificado"}
                      </span>
                    </div>
                  </div>
                  <div className={`GM__${theme}-field-group`}>
                    <div className={`GM__${theme}-field-full`}>
                      <span className={`GM__${theme}-field-label`}>
                        Teléfono de Emergencia:
                      </span>
                      <span className={`GM__${theme}-field-value`}>
                        {user?.emergency_contact_phone || "No especificado"}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div className={`GM__${theme}-no-data`}>
                  <span className={`GM__${theme}-no-data-text`}>
                    No hay información de contacto de emergencia registrada
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* NUEVA SECCIÓN: Información de salud y seguridad social */}
          <div className={`GM__${theme}-info-card`}>
            <h3 className={`GM__${theme}-card-title`}>
              Salud y Seguridad Social
            </h3>
            <div className={`GM__${theme}-card-content`}>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    EPS (Entidad Promotora de Salud):
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {user?.eps || "No especificada"}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    ARL (Administradora de Riesgos Laborales):
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {user?.arl || "No especificada"}
                  </span>
                </div>
              </div>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field-full`}>
                  <span className={`GM__${theme}-field-label`}>
                    Tipo de Sangre:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {user?.blood_type || "No especificado"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Información de rol y estado - Actualizada */}
          <div className={`GM__${theme}-info-card`}>
            <h3 className={`GM__${theme}-card-title`}>
              Rol y Estado del Usuario
            </h3>
            <div className={`GM__${theme}-card-content`}>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Rol:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {user?.role_name || "Sin rol"}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Código de rol:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {user?.role_code || "N/A"}
                  </span>
                </div>
              </div>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Estado:
                  </span>
                  <span className={`GM__${theme}-field-value ${userStatusInfo.className}`}>
                    {userStatusInfo.status}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Razón del estado:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {user?.status_reason || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Información de verificación - Sin cambios */}
          <div className={`GM__${theme}-info-card`}>
            <h3 className={`GM__${theme}-card-title`}>
              Validación y Configuraciones
            </h3>
            <div className={`GM__${theme}-card-content`}>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Email Verificado:
                  </span>
                  <span
                    className={`GM__${theme}-field-value ${
                      user?.verify_email ? "active" : "inactive"
                    }`}
                  >
                    {user?.verify_email ? "Sí" : "No"}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Términos Aceptados:
                  </span>
                  <span
                    className={`GM__${theme}-field-value ${
                      user?.terms_accepted ? "active" : "inactive"
                    }`}
                  >
                    {user?.terms_accepted ? "Sí" : "No"}
                  </span>
                </div>
              </div>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field-full`}>
                  <span className={`GM__${theme}-field-label`}>
                    Política de Privacidad:
                  </span>
                  <span
                    className={`GM__${theme}-field-value ${
                      user?.privacy_policy_accepted ? "active" : "inactive"
                    }`}
                  >
                    {user?.privacy_policy_accepted ? "Aceptada" : "Pendiente"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Información de ubicaciones - Sin cambios */}
          <div className={`GM__${theme}-info-card`}>
            <h3 className={`GM__${theme}-card-title`}>
              Ubicaciones Registradas
            </h3>
            <div className={`GM__${theme}-card-content`}>
              {locationData.length > 0 ? (
                <>
                  <div className={`GM__${theme}-location-info`}>
                    <div className={`GM__${theme}-field-group`}>
                      <div className={`GM__${theme}-field-full`}>
                        <span className={`GM__${theme}-field-label`}>
                          Dirección principal:
                        </span>
                        <span className={`GM__${theme}-field-value`}>
                          {locationData[0]?.address_details || "No disponible"}
                        </span>
                      </div>
                    </div>
                    <div className={`GM__${theme}-field-group`}>
                      <div className={`GM__${theme}-field`}>
                        <span className={`GM__${theme}-field-label`}>
                          Ciudad:
                        </span>
                        <span className={`GM__${theme}-field-value`}>
                          {locationData[0]?.city || "No disponible"}
                        </span>
                      </div>
                      <div className={`GM__${theme}-field`}>
                        <span className={`GM__${theme}-field-label`}>
                          Departamento:
                        </span>
                        <span className={`GM__${theme}-field-value`}>
                          {locationData[0]?.department || "No disponible"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={`GM__${theme}-view-more-section`}>
                    <button
                      className={`GM__${theme}-view-more-btn`}
                      onClick={() => handleOpenDrawer("locations")}
                    >
                      Ver todas las ubicaciones ({locationData.length})
                    </button>
                  </div>
                </>
              ) : (
                <div className={`GM__${theme}-no-data`}>
                  <span className={`GM__${theme}-no-data-text`}>
                    No hay ubicaciones registradas
                  </span>
                  <button
                    className={`GM__${theme}-add-btn`}
                    onClick={() => handleOpenDrawer("locations")}
                  >
                    Agregar ubicación
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sección de observaciones - Sin cambios */}
        <div className={`GM__${theme}-observations-section`}>
          <h3 className={`GM__${theme}-section-title`}>Observaciones</h3>
          <div className={`GM__${theme}-observations-content`}>
            {commentData.length > 0 ? (
              <div className={`GM__${theme}-observations-cards-container`}>
                {/* Renderizar máximo 3 comentarios */}
                {commentData.slice(0, 3).map((comment) => (
                  <div 
                    key={comment.id} 
                    className={`GM__${theme}-observation-card`}
                    onClick={() => handleOpenDrawer("comments")}
                  >
                    <div className={`GM__${theme}-observation-card-content`}>
                      <span className={`GM__${theme}-observation-title`}>
                        {comment.title || 'Sin título'}
                      </span>
                    </div>
                  </div>
                ))}
                
                {/* Card de "Ver todos" como cuarta card */}
                <div 
                  className={`GM__${theme}-observation-card ${`GM__${theme}-add-more-card`}`}
                  onClick={() => handleOpenDrawer("comments")}
                >
                  <div className={`GM__${theme}-observation-card-content`}>
                    <span className={`GM__${theme}-add-more-text`}>
                      Ver todos ({commentData.length})
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className={`GM__${theme}-no-observations`}>
                <div 
                  className={`GM__${theme}-observation-card ${`GM__${theme}-add-comment-card`}`}
                  onClick={() => handleOpenDrawer("comments")}
                >
                  <div className={`GM__${theme}-observation-card-content`}>
                    <span className={`GM__${theme}-add-comment-text`}>
                      Agregar comentario
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <CommentsLocationsDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        type={drawerType}
        data={drawerType === "comments" ? commentData : locationData}
        onEdit={handleEdit}
        onCreate={handleCreate}
        onDelete={handleDelete}
        theme={theme}
      />
    </div>
  );
};

// PropTypes actualizados con todos los nuevos campos
UserProfileInfo.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    first_name: PropTypes.string,
    first_last_name: PropTypes.string,
    second_last_name: PropTypes.string,
    email: PropTypes.string,
    document_type_name: PropTypes.string,
    document_number: PropTypes.string,
    document_exp_date: PropTypes.string,
    document_expedition_place: PropTypes.string,
    birth_date: PropTypes.string,
    primary_phone_number: PropTypes.string,
    primary_phone_code: PropTypes.string,
    secondary_phone_number: PropTypes.string,
    secondary_phone_code: PropTypes.string,
    role_name: PropTypes.string,
    role_code: PropTypes.string,
    nationality_country: PropTypes.string,
    active_status: PropTypes.bool,
    verify_email: PropTypes.bool,
    terms_accepted: PropTypes.bool,
    privacy_policy_accepted: PropTypes.bool,
    status_reason: PropTypes.string,
    // Nuevos campos agregados
    department: PropTypes.string,
    blood_type: PropTypes.string,
    job_position: PropTypes.string,
    emergency_contact_name: PropTypes.string,
    emergency_contact_phone: PropTypes.string,
    limited_notifications: PropTypes.bool,
    own_fleet: PropTypes.bool,
    eps: PropTypes.string,
    arl: PropTypes.string,
  }).isRequired,
  canEditProfile: PropTypes.bool.isRequired,
  onEditClick: PropTypes.func.isRequired,
  profileImageId: PropTypes.string,
  theme: PropTypes.oneOf(['dark', 'light']).isRequired,
};

export default UserProfileInfo;